/**
 * Admin Gender Party List Page
 * Lists all gender parties with management options
 * Supports filtering by eventId via URL query parameter
 */

import { useEffect, useMemo } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/layout";
import { PageLoader } from "@/components/PageLoader";
import { IconAlertTriangle, IconPlus, IconChevronRight, IconChevronLeft } from "@/components/Icons";
import { getTelegram } from "@/lib/telegram";
import {
  useAdminGenderParties,
  useAdminGenderPartyEvents,
  type GenderParty,
  type GenderPartyStatus,
} from "./hooks";

function getStatusLabel(status: GenderPartyStatus): string {
  switch (status) {
    case "DRAFT":
      return "Черновик";
    case "VOTING_OPEN":
      return "Голосование открыто";
    case "VOTING_CLOSED":
      return "Голосование закрыто";
    case "REVEALED":
      return "Результаты показаны";
    case "COMPLETED":
      return "Завершено";
    default:
      return status;
  }
}

function getStatusColor(status: GenderPartyStatus): string {
  switch (status) {
    case "DRAFT":
      return "bg-slate-100 text-slate-600";
    case "VOTING_OPEN":
      return "bg-green-100 text-green-700";
    case "VOTING_CLOSED":
      return "bg-amber-100 text-amber-700";
    case "REVEALED":
      return "bg-blue-100 text-blue-700";
    case "COMPLETED":
      return "bg-purple-100 text-purple-700";
    default:
      return "bg-slate-100 text-slate-600";
  }
}

function GenderPartyCard({ genderParty }: { genderParty: GenderParty }) {
  return (
    <Link
      to={`/admin/gender-party/${genderParty.id}`}
      className="block bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-900 truncate">
            {genderParty.name}
          </h3>
          <p className="text-sm text-slate-500 truncate">
            {genderParty.partnerOneName} & {genderParty.partnerTwoName}
          </p>
        </div>
        <IconChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(
            genderParty.status
          )}`}
        >
          {getStatusLabel(genderParty.status)}
        </span>

        {genderParty.isTest && (
          <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-700 font-medium">
            Тест
          </span>
        )}

        {genderParty.voteCounts && (
          <span className="text-xs text-slate-500">
            {genderParty.voteCounts.totalVotes} голосов
          </span>
        )}
      </div>

      {genderParty.event && (
        <p className="text-xs text-slate-400 mt-2 truncate">
          {genderParty.event.title}
        </p>
      )}
    </Link>
  );
}

export function AdminGenderPartyListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tg = getTelegram();

  const eventIdFilter = searchParams.get("eventId");
  const { genderParties, isLoading, error } = useAdminGenderParties(eventIdFilter);
  const { events } = useAdminGenderPartyEvents();

  const selectedEvent = useMemo(() => {
    if (!eventIdFilter) return null;
    return events.find((e) => e.id === eventIdFilter) ?? null;
  }, [eventIdFilter, events]);

  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (eventIdFilter) {
        navigate(`/admin/event/${eventIdFilter}`);
      } else {
        navigate("/admin");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate, eventIdFilter]);

  if (isLoading) {
    return (
      <Layout>
        <PageLoader message="Загрузка..." />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center">
            <IconAlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <p className="text-slate-600">Ошибка загрузки</p>
            <p className="text-sm text-slate-500 mt-1">{error}</p>
          </div>
        </div>
      </Layout>
    );
  }

  const createLink = eventIdFilter
    ? `/admin/gender-party/new?eventId=${eventIdFilter}`
    : "/admin/gender-party/new";

  return (
    <Layout>
      <div className="min-h-screen bg-slate-50">
        <div className="bg-white border-b border-slate-200 px-4 py-4 sticky top-0 z-10">
          {selectedEvent && (
            <button
              onClick={() => navigate(`/admin/event/${eventIdFilter}`)}
              className="flex items-center gap-1 text-sm text-slate-500 mb-2 hover:text-slate-700"
            >
              <IconChevronLeft className="w-4 h-4" />
              {selectedEvent.title}
            </button>
          )}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-900">Gender Party</h1>
              <p className="text-sm text-slate-500">
                {selectedEvent
                  ? `Голосования для мероприятия`
                  : "Управление голосованиями"}
              </p>
            </div>
            <Link
              to={createLink}
              className="flex items-center gap-1 px-3 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
            >
              <IconPlus className="w-4 h-4" />
              Создать
            </Link>
          </div>
        </div>

        <div className="p-4 space-y-3">
          {genderParties.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-3">🗳️</div>
              <p className="text-slate-600 mb-4">
                {selectedEvent
                  ? "Нет голосований для этого мероприятия"
                  : "Нет голосований"}
              </p>
              <Link
                to={createLink}
                className="inline-flex items-center gap-1 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
              >
                <IconPlus className="w-4 h-4" />
                Создать первое
              </Link>
            </div>
          ) : (
            genderParties.map((gp) => (
              <GenderPartyCard key={gp.id} genderParty={gp} />
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}
